/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';

const C = {
  bg: 'rgba(8,12,30,0.60)',
  kant: 'rgba(255,255,255,0.08)',
  tekst: '#e2e8f0',
  subtekst: '#7a8fa6',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
};

const TYPE_LABELS = {
  'foraeldre myndighed': 'Forældremyndighed',
  foraeldre_myndighed: 'Forældremyndighed',
  bopael: 'Bopæl',
  'Bøpæl': 'Bopæl',
  skilsmisse: 'Skilsmisse',
  'Skilsmisse': 'Skilsmisse',
  samvaer: 'Samvær',
  'Samvær': 'Samvær',
  myndighedsdialog: 'Myndighedsdialog',
  'Bodeling': 'Bodeling',
  'Rådgivning': 'Rådgivning',
  'Vilkårsforhandling': 'Vilkårsforhandling',
  'Bopæl': 'Bopæl',
  andet: 'Andet',
};

const api = async (sti) => {
  const res = await fetch('/api' + sti, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const statusFarve = (status) => {
  if (status === 'under_median') return C.success;
  if (status === 'over_median') return C.warning;
  if (status === 'over_p75') return C.danger;
  return C.subtekst;
};

const statusLabel = (status) => {
  if (status === 'under_median') return 'Under median';
  if (status === 'over_median') return 'Median – P75';
  if (status === 'over_p75') return 'Over P75';
  return 'Ukendt type';
};

export default function SagstidsPrognoseDSPanel() {
  const [data, setData] = useState(null);
  const [loader, setLoader] = useState(true);
  const [fejl, setFejl] = useState(null);
  const [valgtType, setValgtType] = useState(null);

  useEffect(() => {
    api('/os/ds/sagstid-prognose')
      .then(d => { setData(d); setLoader(false); })
      .catch(e => { setFejl(e.message); setLoader(false); });
  }, []);

  if (loader) return <div style={{ padding: 32, color: C.subtekst, textAlign: 'center' }}>Henter sagstidsdata…</div>;
  if (fejl) return <div style={{ padding: 32, color: '#ef4444' }}>Fejl: {fejl}</div>;
  if (!data) return null;

  const statistik = data.statistik || [];
  const aktive = data.aktive || [];

  const chartData = statistik.map(s => ({
    type: TYPE_LABELS[s.type] || s.type,
    typeRaw: s.type,
    median: s.median_dage,
    overMedian: Math.max(0, s.p75_dage - s.median_dage),
    p75: s.p75_dage,
    p90: s.p90_dage,
    antal: s.antal_afsluttede,
  }));

  const aktiveSagerForType = valgtType ? aktive.filter(a => a.type === valgtType) : aktive;
  const totalAktive = aktive.length;
  const overP75 = aktive.filter(a => a.status === 'over_p75').length;
  const underMedian = aktive.filter(a => a.status === 'under_median').length;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, color: C.tekst, fontSize: 20, fontWeight: 800 }}>Sagstids-Prognose per Sagstype</h2>
          <div style={{ fontSize: 12, color: C.subtekst, marginTop: 4 }}>
            Baseret på {statistik.reduce((a, s) => a + s.antal_afsluttede, 0)} afsluttede sager
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div style={{ flex: 1, minWidth: 140, background: C.bg, border: `1px solid ${C.kant}`, borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontSize: 12, color: C.subtekst, fontWeight: 600, marginBottom: 4 }}>Aktive sager</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.tekst }}>{totalAktive}</div>
        </div>
        <div style={{ flex: 1, minWidth: 140, background: C.bg, border: `1px solid ${C.kant}`, borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontSize: 12, color: C.subtekst, fontWeight: 600, marginBottom: 4 }}>Under median</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.success }}>{underMedian}</div>
        </div>
        <div style={{ flex: 1, minWidth: 140, background: C.bg, border: `1px solid ${C.kant}`, borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontSize: 12, color: C.subtekst, fontWeight: 600, marginBottom: 4 }}>Over P75 (lang)</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: overP75 > 0 ? C.danger : C.tekst }}>{overP75}</div>
        </div>
      </div>

      {chartData.length > 0 ? (
        <div style={{ background: C.bg, border: `1px solid ${C.kant}`, borderRadius: 12, padding: '16px 8px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.tekst, marginBottom: 12, paddingLeft: 8 }}>
            Median (grøn) + P75-median (orange) per sagstype
          </div>
          <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 48)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: 60, left: 8, bottom: 4 }}
              onClick={(e) => {
                if (e?.activePayload?.[0]?.payload?.typeRaw) {
                  const t = e.activePayload[0].payload.typeRaw;
                  setValgtType(prev => prev === t ? null : t);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
              <XAxis type="number" tick={{ fill: C.subtekst, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}d`} />
              <YAxis type="category" dataKey="type" tick={{ fill: C.tekst, fontSize: 11 }} axisLine={false} tickLine={false} width={120} />
              <Tooltip
                contentStyle={{ background: '#0a0f23', border: `1px solid ${C.kant}`, borderRadius: 8, fontSize: 12 }}
                formatter={(value, name) => {
                  const labels = { median: 'Median', overMedian: 'P75 – Median' };
                  return [`${value} dage`, labels[name] || name];
                }}
                labelFormatter={(label) => {
                  const s = chartData.find(c => c.type === label);
                  return `${label} (P90: ${s?.p90 || '—'}d, ${s?.antal || 0} sager)`;
                }}
              />
              <Bar dataKey="median" stackId="a" fill={C.success} name="median">
                <LabelList dataKey="median" position="insideLeft" style={{ fill: '#fff', fontSize: 10 }} formatter={v => v > 0 ? `${v}d` : ''} />
              </Bar>
              <Bar dataKey="overMedian" stackId="a" fill={C.warning} name="overMedian" radius={[0, 4, 4, 0]}>
                <LabelList dataKey="p75" position="right" style={{ fill: C.warning, fontSize: 10 }} formatter={v => `P75:${v}d`} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: C.subtekst, paddingLeft: 8, marginTop: 4 }}>
            Klik på en bar for at filtrere aktive sager nedenfor
          </div>
        </div>
      ) : (
        <div style={{ color: C.subtekst, padding: 20, textAlign: 'center', fontSize: 13 }}>
          Ingen afsluttede sager med tilstrækkelig data endnu.
        </div>
      )}

      <div style={{ background: C.bg, border: `1px solid ${C.kant}`, borderRadius: 12, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.tekst }}>
            Aktive sager — {valgtType ? (TYPE_LABELS[valgtType] || valgtType) : 'alle typer'}
          </div>
          {valgtType && (
            <button onClick={() => setValgtType(null)} style={{
              background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.kant}`,
              color: C.subtekst, borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer',
            }}>
              Vis alle
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
          {[
            { status: 'under_median', label: 'Under median' },
            { status: 'over_median', label: 'Median – P75' },
            { status: 'over_p75', label: 'Over P75 (lang)' },
          ].map(l => (
            <div key={l.status} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.subtekst }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: statusFarve(l.status), display: 'inline-block' }} />
              {l.label}
            </div>
          ))}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {['Sag ID', 'Type', 'Dage brugt', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: C.subtekst, fontWeight: 700, borderBottom: `1px solid ${C.kant}`, whiteSpace: 'nowrap', fontSize: 11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {aktiveSagerForType.slice(0, 20).map((a, i) => (
                <tr key={i} style={{ borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
                  <td style={{ padding: '6px 10px', color: C.subtekst }}>#{a.id}</td>
                  <td style={{ padding: '6px 10px', color: C.tekst }}>{TYPE_LABELS[a.type] || a.type}</td>
                  <td style={{ padding: '6px 10px', color: C.tekst, fontWeight: 600 }}>{a.dage_brugt}d</td>
                  <td style={{ padding: '6px 10px' }}>
                    <span style={{
                      display: 'inline-block',
                      background: `${statusFarve(a.status)}1a`,
                      border: `1px solid ${statusFarve(a.status)}44`,
                      color: statusFarve(a.status),
                      borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700,
                    }}>
                      {statusLabel(a.status)}
                    </span>
                  </td>
                </tr>
              ))}
              {aktiveSagerForType.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: '16px 10px', textAlign: 'center', color: C.subtekst }}>
                    {valgtType ? 'Ingen aktive sager af denne type' : 'Ingen aktive sager'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {aktiveSagerForType.length > 20 && (
          <div style={{ fontSize: 11, color: C.subtekst, marginTop: 8, textAlign: 'right' }}>
            Viser 20 af {aktiveSagerForType.length} sager
          </div>
        )}
      </div>
    </div>
  );
}
